import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useState, useCallback } from "react";
import logoEntrelaces from "@/assets/logo-entrelaces.png";
import { Button } from "./ui/button";
import { useWarningPopup } from "@/contexts/WarningPopupContext";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const warningPopup = useWarningPopup();
  const location = useLocation();
  const navigate = useNavigate();

  const scrollToSection = useCallback((sectionId: string, path: string) => {
    const scrollToElement = () => {
      const element = document.getElementById(sectionId);
      if (element) {
        const headerHeight = 112;
        const elementPosition = element.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({
          top: elementPosition - headerHeight,
          behavior: "smooth",
        });
      }
    };

    if (location.pathname === "/" || location.pathname === path) {
      window.history.pushState({}, "", path);
      scrollToElement();
    } else {
      navigate(path);
      setTimeout(scrollToElement, 100);
    }
    setIsMenuOpen(false);
  }, [location.pathname, navigate]);

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16 md:h-28">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <img 
                src={logoEntrelaces} 
                alt="Entrelaces Psicologia" 
                className="h-12 md:h-24 w-auto"
                width={95}
                height={80}
                sizes="80px"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8">
              <button
                onClick={() => scrollToSection("inicio", "/")}
                className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
              >
                Início
              </button>
              <button
                onClick={() => scrollToSection("sobre", "/sobre")}
                className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
              >
                Sobre
              </button>
              <button
                onClick={() => scrollToSection("servicos", "/servicos")}
                className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
              >
                Serviços
              </button>
              <button
                onClick={() => scrollToSection("localizacao", "/localizacao")}
                className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
              >
                Localização
              </button>
            </nav>

            {/* CTA Button */}
            <div className="hidden md:flex items-center">
              <Button variant="hero" size="lg" onClick={warningPopup.open}>
                Agendar pelo WhatsApp
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 text-foreground"
              aria-label={isMenuOpen ? "Fechar menu" : "Abrir menu de navegação"}
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <div className="md:hidden py-4 border-t border-border animate-fade-in">
              <nav className="flex flex-col gap-4">
                <button
                  onClick={() => scrollToSection("inicio", "/")}
                  className="text-base font-medium text-foreground hover:text-primary transition-colors px-2 py-2 text-left"
                >
                  Início
                </button>
                <button
                  onClick={() => scrollToSection("sobre", "/sobre")}
                  className="text-base font-medium text-foreground hover:text-primary transition-colors px-2 py-2 text-left"
                >
                  Sobre
                </button>
                <button
                  onClick={() => scrollToSection("servicos", "/servicos")}
                  className="text-base font-medium text-foreground hover:text-primary transition-colors px-2 py-2 text-left"
                >
                  Serviços
                </button>
                <button
                  onClick={() => scrollToSection("localizacao", "/localizacao")}
                  className="text-base font-medium text-foreground hover:text-primary transition-colors px-2 py-2 text-left"
                >
                  Localização
                </button>
                <div className="pt-4 border-t border-border">
                  <Button 
                    variant="hero" 
                    size="lg" 
                    className="w-full"
                    onClick={() => {
                      setIsMenuOpen(false);
                      warningPopup.open();
                    }}
                  >
                    Agendar pelo WhatsApp
                  </Button>
                </div>
              </nav>
            </div>
          )}
        </div>
      </header>
    </>
  );
};

export default Header;
